import { React, useEffect } from "react";
import Box from "@material-ui/core/Box";
import { FormHelperText, Grid, TextField, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import {
	getGameType,
	getMatchResult,
	IncompleteType,
	isSetTiebreakEligible,
	getTiebreakAdjustedSet,
	isSetDisabled,
	SetType,
	TennisTeamSide,
	MatchResultType
} from "@prestosports/prestostats-core/Tennis";
import TrophyFilled16 from "@carbon/icons-react/lib/trophy--filled/16";
import { useTeamsContext } from "../../../state/teams/context";
import GameSet from "./GameSet";
import { usePlayersTennisContext } from "../../../state/players/tennis-context";
import MatchEditStyles from "../styles/MatchEdit.styles";
import { useEventContext } from "../../../state/events/context";
import gameSetStyles from "../styles/GameSet.style";
import MatchPlayers from "./MatchPlayers";
import MatchTiebreak from "./MatchTiebreak";
import IncompleteButtonGroup from "./IncompleteButtonGroup";
import MatchSettings from "./MatchSettings";
import DurationPicker from "../../common/components/DurationPicker";

const RANK_MAX = 999;

const MatchEdit = ({ match = {}, onValidate = () => {}, showRank = true }) => {
	const classes = MatchEditStyles();
	const gameSetClasses = gameSetStyles();

	const { state: events } = useEventContext();
	const currentEvent = events.currentEvent() || {};

	const { state: teamState } = useTeamsContext();
	const homeTeam = teamState.homeTeam();
	const awayTeam = teamState.awayTeam();
	const { state: homePlayersTennisState } = usePlayersTennisContext(homeTeam);
	const { state: awayPlayersTennisState } = usePlayersTennisContext(awayTeam);
	const { gameConfiguration } = events.currentEventSettings() || {};
	const homeSinglePlayers = homePlayersTennisState.single() || [];
	const awaySinglePlayers = awayPlayersTennisState.single() || [];
	const homeDoublesPlayers = homePlayersTennisState.doubles() || [];
	const awayDoublesPlayers = awayPlayersTennisState.doubles() || [];
	const homeNAPlayers = homePlayersTennisState.naPlayers() || [];
	const awayNAPlayers = awayPlayersTennisState.naPlayers() || [];
	const gameType = getGameType(match, gameConfiguration);
	const setsToPlay = gameType.value;
	const { isSingle } = match;

	const validateSets = (sets) => {
		const errors = {};
		for (let i = 0; i < sets.length && !errors.away; i += 1) {
			const { away, home } = sets[i];
			if (away?.tiebreak && home?.tiebreak && away?.tiebreak === home?.tiebreak) {
				errors.away = "Tiebreak cannot be equal";
			}
		}

		return errors;
	};

	const validateScore = (currentMatch) => {
		const { sets, tiebreakSets } = currentMatch;

		return {
			...validateSets(sets),
			...validateSets(tiebreakSets)
		};
	};

	const validatePlayers = (currentMatch) => {
		const errors = {};
		if (isSingle) {
			if (!currentMatch.away.players.length) {
				errors.away = `No Player Selected for ${awayTeam.teamName}`;
			}
			if (!currentMatch.home.players.length) {
				errors.home = `No Player Selected for ${homeTeam.teamName}`;
			}
		} else {
			if (
				currentMatch.away.players.length !== 2 &&
				!currentMatch.away.players.some((pId) =>
					awayNAPlayers.some((naP) => pId === naP.id)
				)
			) {
				errors.away = `${awayTeam.teamName} Requires Two Players Selected`;
			}
			if (
				currentMatch.home.players.length !== 2 &&
				!currentMatch.home.players.some((pId) =>
					homeNAPlayers.some((naP) => pId === naP.id)
				)
			) {
				errors.home = `${homeTeam.teamName} Requires Two Players Selected`;
			}
		}

		return errors;
	};

	const emptySet = {
		away: { score: null, tiebreak: null },
		home: { score: null, tiebreak: null }
	};

	const formik = useFormik({
		enableReinitialize: false,
		isInitialValid: false,
		// validateOnMount: true, // DO NOT USE THIS! It causes rerenders and I don't know how to fix it.
		initialValues: {
			matchNumber: null,
			isShortened: false,
			away: {
				players: [],
				teamId: awayTeam.id,
				rank: null,
				incompleteType: null
			},
			home: {
				players: [],
				teamId: homeTeam.id,
				rank: null,
				incompleteType: null
			},
			sets: [...Array(setsToPlay).keys()].map((_, index) => ({
				...emptySet,
				index
			})),
			tiebreakSets: [],
			matchTiebreakType: undefined,
			tiebreakPoints: null,
			eventId: currentEvent.id,
			isSingle,
			duration: "00:00:00",
			...match
		},
		validate: (values) => {
			const playerErrors = validatePlayers(values);
			const scoreErrors = validateScore(values);

			return { ...playerErrors, ...scoreErrors };
		}
	});

	const { values, setFieldValue, setTouched, touched, errors, setValues } =
		formik;
	const matchResult = getMatchResult(values, gameConfiguration);

	useEffect(() => {
		onValidate({
			values,
			isValid:
				Object.values(touched).length >= 1 && Object.values(errors).length === 0
		});
	}, [values, touched, errors]);

	const updateSet = (key, index, gameSet, teamSide) => {
		const newValues = values[key].map((item, i) => {
			if (i === index) {
				const opponentSide =
					teamSide === TennisTeamSide.Home.code
						? TennisTeamSide.Away.code
						: TennisTeamSide.Home.code;
				const newSet = Object.assign(item, {
					[teamSide]: getTiebreakAdjustedSet(
						item[teamSide],
						gameSet,
						item[opponentSide],
						key,
						match,
						gameConfiguration
					)
				});
				if (!isSetTiebreakEligible(values[key][i], match, gameConfiguration)) {
					newSet[teamSide].tiebreak = null;
					newSet[opponentSide] = getTiebreakAdjustedSet(
						newSet[opponentSide],
						{ ...newSet[opponentSide], tiebreak: null },
						item[teamSide],
						key,
						match,
						gameConfiguration
					);
				}

				return newSet;
			}

			return item;
		});
		setTouched({ [key]: true }, true);
		setFieldValue(key, newValues);
	};

	const setMatchTiebreakType = (value) => {
		setTouched({ matchTiebreakType: true }, false);
		setFieldValue("matchTiebreakType", value?.code);
	};

	const setRank = (rank, teamSide) => {
		if (Number(rank) >= 0 && Number(rank) <= RANK_MAX) {
			setTouched({ ...touched, [teamSide]: true });
			setFieldValue(teamSide, {
				...values[teamSide],
				rank
			});
		}
	};

	const setDuration = (value) => {
		setTouched({ duration: true });
		setFieldValue("duration", value);
	};

	const setPlayers = (players, teamSide) => {
		const naIds = (
			teamSide === TennisTeamSide.Home.code ? homeNAPlayers : awayNAPlayers
		).map((p) => p.id);
		const containsNoPlayer = players.some((pId) =>
			naIds.some((naId) => naId === pId)
		);
		setTouched({ ...touched, [teamSide]: true });
		setFieldValue(teamSide, {
			...values[teamSide],
			noPlayer: containsNoPlayer,
			players
		});
	};

	const addMatchTiebreakSet = () => {
		setTouched({ tiebreakSets: true }, false);
		setFieldValue("tiebreakSets", [
			...(values.tiebreakSets || []),
			{ ...emptySet }
		]);
	};

	const removeMatchTiebreakSet = (index) => {
		setTouched({ tiebreakSets: true }, false);
		setFieldValue(
			"tiebreakSets",
			values.tiebreakSets?.filter((t, i) => i !== index)
		);
	};

	const setMatchTiebreakPoints = (value, teamSide) => {
		setTouched({ tiebreakPoints: true }, false);
		setFieldValue("tiebreakPoints", {
			...values.tiebreakPoints,
			[teamSide]: value
		});
	};

	const toggleIncompleteType = (type, teamSide) => {
		const toggleValue = type.equals(values[teamSide].incompleteType)
			? null
			: type;
		const opposingType =
			teamSide === TennisTeamSide.Home.code
				? values.away.incompleteType
				: values.home.incompleteType;

		let defaultType = null;
		if (
			IncompleteType.DQ.equals(type) &&
			IncompleteType.DQ.equals(opposingType)
		) {
			defaultType = IncompleteType.DQ;
		} else if (
			IncompleteType.DNF.equals(type) ||
			IncompleteType.NotPlayed.equals(type)
		) {
			defaultType = toggleValue;
		}

		setTouched({ ...touched, [teamSide]: true });
		setValues({
			...values,
			home: {
				...values.home,
				incompleteType:
					teamSide === TennisTeamSide.Home.code ? toggleValue : defaultType
			},
			away: {
				...values.away,
				incompleteType:
					teamSide === TennisTeamSide.Away.code ? toggleValue : defaultType
			}
		});
	};

	return (
		<form>
			<MatchSettings match={match} />
			<Grid container className={classes.form}>
				<Grid spacing={1} container item>
					<Grid xs={12} sm={6} item className={classes.playerContainer}>
						{showRank && (
							<TextField
								className={gameSetClasses.score}
								style={{ width: 45, margin: "0 5px" }}
								aria-label="Away rank"
								id="rankAway"
								variant="outlined"
								key="rank-away"
								type="number"
								InputLabelProps={{
									shrink: true
								}}
								inputProps={{ "aria-label": "Away team rank" }}
								value={values.away.rank}
								onChange={(e) => setRank(e.target.value, TennisTeamSide.Away.code)}
								placeholder="-"
							/>
						)}
						<MatchPlayers
							isSingle={isSingle}
							team={awayTeam}
							singlePlayers={awaySinglePlayers}
							doublesPlayers={awayDoublesPlayers}
							currentPlayers={values.away.players}
							onChange={(players) => setPlayers(players, TennisTeamSide.Away.code)}
							startAdornment={
								matchResult === MatchResultType.Away.code && <TrophyFilled16 />
							}
						/>
					</Grid>
					<Grid xs={12} sm={6} item className={classes.playerContainer}>
						<MatchPlayers
							isSingle={isSingle}
							team={homeTeam}
							singlePlayers={homeSinglePlayers}
							doublesPlayers={homeDoublesPlayers}
							currentPlayers={values.home.players}
							onChange={(players) => setPlayers(players, TennisTeamSide.Home.code)}
							startAdornment={
								matchResult === MatchResultType.Home.code && <TrophyFilled16 />
							}
						/>
						{showRank && (
							<TextField
								className={gameSetClasses.score}
								style={{ width: 45, margin: "0 5px" }}
								aria-label="Home rank"
								id="rankHome"
								variant="outlined"
								key="rank-home"
								type="number"
								InputLabelProps={{
									shrink: true
								}}
								inputProps={{ "aria-label": "Home team rank" }}
								value={values.home.rank}
								onChange={(e) => setRank(e.target.value, TennisTeamSide.Home.code)}
								placeholder="-"
							/>
						)}
					</Grid>
				</Grid>
				<Box
					display="flex"
					style={{ width: "100%" }}
					justifyContent="center"
					mt={1}
				>
					<Typography className={gameSetClasses.columnTitle}>Score</Typography>
					<Typography className={gameSetClasses.columnTitle}>Tiebreak</Typography>
					<div className={gameSetClasses.spacing} />
					<Typography className={gameSetClasses.columnTitle}>Score</Typography>
					<Typography className={gameSetClasses.columnTitle}>Tiebreak</Typography>
				</Box>
				<Box display="flex" flexDirection="column" style={{ margin: "0 58px 0 0" }}>
					{values.sets.map((set) => {
						const { index } = set;
						const disabled = isSetDisabled(set, values, gameConfiguration);

						return (
							<GameSet
								key={`${match.id}-${index}`}
								onAwayScoreChange={(gameSet) =>
									updateSet(SetType.Sets.code, index, gameSet, TennisTeamSide.Away.code)
								}
								onAwayTiebreakChange={(gameSet) =>
									updateSet(SetType.Sets.code, index, gameSet, TennisTeamSide.Away.code)
								}
								onHomeScoreChange={(gameSet) =>
									updateSet(SetType.Sets.code, index, gameSet, TennisTeamSide.Home.code)
								}
								onHomeTiebreakChange={(gameSet) =>
									updateSet(SetType.Sets.code, index, gameSet, TennisTeamSide.Home.code)
								}
								disabled={disabled}
								index={index}
								match={values}
								set={set}
							/>
						);
					})}
				</Box>
				<Box
					display="flex"
					style={{ width: "100%" }}
					justifyContent="center"
					mb={1}
				>
					<IncompleteButtonGroup
						home={values.home}
						away={values.away}
						toggleIncompleteType={toggleIncompleteType}
					/>
				</Box>
				<Box style={{ width: "100%", padding: 20 }}>
					<MatchTiebreak
						match={values}
						onAddSet={addMatchTiebreakSet}
						onRemoveSet={removeMatchTiebreakSet}
						onAwayScoreChange={(index, gameSet) =>
							updateSet(
								SetType.TiebreakSets.code,
								index,
								gameSet,
								TennisTeamSide.Away.code
							)
						}
						onAwayTiebreakChange={(index, gameSet) =>
							updateSet(
								SetType.TiebreakSets.code,
								index,
								gameSet,
								TennisTeamSide.Away.code
							)
						}
						onHomeScoreChange={(index, gameSet) =>
							updateSet(
								SetType.TiebreakSets.code,
								index,
								gameSet,
								TennisTeamSide.Home.code
							)
						}
						onHomeTiebreakChange={(index, gameSet) =>
							updateSet(
								SetType.TiebreakSets.code,
								index,
								gameSet,
								TennisTeamSide.Home.code
							)
						}
						onTypeChange={(matchTiebreakType) => {
							setMatchTiebreakType(matchTiebreakType);
						}}
						onAwayPointsChange={(value) => {
							setMatchTiebreakPoints(value, TennisTeamSide.Away.code);
						}}
						onHomePointsChange={(value) => {
							setMatchTiebreakPoints(value, TennisTeamSide.Home.code);
						}}
					/>
				</Box>
				<Box className={classes.durationParent}>
					<Typography variant="subtitle2">Match Duration</Typography>
					<Box className={classes.durationPicker}>
						<DurationPicker
							handleDurationChange={setDuration}
							value={values.duration}
						/>
					</Box>
				</Box>
			</Grid>
			<Box className={classes.errorMessage} role="status" aria-live="polite">
				{Object.values(errors).map((error) => (
					<FormHelperText key={error} error={Boolean(error)}>
						{error}
					</FormHelperText>
				))}
			</Box>
		</form>
	);
};

// MatchEdit.whyDidYouRender = true;

export default MatchEdit;
